import { inject } from '@angular/core';
import type { CanActivateFn } from '@angular/router';
import { environment } from '@env';
import { TranslateService } from '@ngx-translate/core';
import { CACHE_STORAGE } from '@tvd/cdk/browser';

const CACHE_NAME = 'zynday-translations-v1';
const FETCH_TIMEOUT_MS = 10_000;

/**
 * Route guard that lazily loads a scoped translation file and merges it
 * into the active translations under the given scope key.
 *
 * Usage in routes:
 *   { path: 'quiz', canActivate: [loadScopedTranslations('quiz')], ... }
 */
export function loadScopedTranslations(scope: string): CanActivateFn {
  return async () => {
    const translate = inject(TranslateService);
    const cacheStorage = inject(CACHE_STORAGE);
    const lang =
      translate.getCurrentLang() || translate.getFallbackLang() || 'de';
    const route = environment.envName === 'production' ? '/public' : '/admin';
    const url = `${environment.apiUrl}${route}/translations/languages/${lang}/files/${scope}.json`;

    try {
      const data = await fetchWithCache(url, cacheStorage);
      translate.setTranslation(lang, { [scope]: data } as any, true);
    } catch {
      // Silent failure — main translations remain usable
    }

    return true;
  };
}

async function fetchWithCache(
  url: string,
  cacheStorage: CacheStorage,
): Promise<Record<string, unknown>> {
  const cache = await cacheStorage.open(CACHE_NAME);
  const cached = await cache.match(url);

  if (cached) {
    const json = await cached.clone().json();
    return json.data;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  const response = await fetch(url, { signal: controller.signal });
  clearTimeout(timeoutId);

  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  await cache.put(url, response.clone());

  const json = await response.json();
  return json.data;
}
